import type { NextAction } from "@/lib/api/chat";

const ACTION_ID = /^journey_[0-9a-f]{24}$/;
const CONCERN_ID = /^concern_[0-9a-f]{24}$/;
const SLOTS = new Set(["adaptive_continuation", "primary_monetization", "outbound_reentry"]);

/**
 * Why a governed action could not be rendered. A stable, PII-free enum so the
 * owned js_error beacon (rule 65) can fingerprint the CAUSE, not just the fact.
 *
 * `absent` is the only non-defect member: the producer emitted no action.
 */
type GovernedActionRejection =
  "absent" | "not_an_object" | "identity" | "presentation" | "policy" | "eligibility";

export interface GovernedActionOutcome {
  /** The eligible action, or null when `rejection` explains why not. */
  action: NextAction | null;
  rejection: GovernedActionRejection | null;
  /**
   * The caller handed us the SSE envelope `{type, next_action}` instead of the
   * action itself, and this contract unwrapped it. The seeker still gets the
   * offer; the WIRING defect is named so it can never again masquerade as
   * "the backend emitted an invalid action" (2026-07-19).
   */
  misroutedEnvelope: boolean;
}

/** The single key `post_events.next_action_event` wraps the action in. */
const STREAM_ENVELOPE_KEY = "next_action";

/**
 * Resolve the governed action from ANY layer of the wire the producer emits.
 *
 * `post_events.next_action_event` puts an ENVELOPE on the wire —
 * `sse({"type": "next_action", "next_action": action})`. The canonical
 * api-client dispatcher unwraps it (`handlers.onNextAction(event.next_action)`),
 * but nothing in the type system stops a consumer from passing the envelope: an
 * envelope and an action are both `Record<string, unknown>`. On 2026-07-19 a
 * Vercel-SDK stream hook did exactly that with `part.data`; an envelope has no
 * `action_id`, so canonical identity failed on EVERY action, each one was
 * discarded, and the beacon reported "the stream returned an invalid governed
 * action" — 858 distinct clients on 07-20 alone (10.45% of daily clients vs a
 * 0.36-0.54% baseline), for 45 hours, on the top of the revenue funnel.
 *
 * The permanent fix is at the contract, not at the one call site that regressed:
 * the contract now accepts BOTH shapes the producer can put on the wire, so a
 * layer mismatch cannot silently destroy a valid action. It stays observable —
 * `misroutedEnvelope` names the wiring defect, and `rejection` names which
 * governance predicate failed — so an alarm can distinguish "the client is wired
 * to the wrong layer" from "the backend emitted something ineligible".
 *
 * Exactly one unwrap: an action nested two envelopes deep is not a shape the
 * producer can emit, and an unbounded walk would launder genuinely broken data.
 */
export function resolveGovernedAction(value: unknown): GovernedActionOutcome {
  const { candidate, misroutedEnvelope } = unwrapStreamEnvelope(value);
  const reject = (rejection: GovernedActionRejection): GovernedActionOutcome => ({
    action: null,
    rejection,
    misroutedEnvelope,
  });
  if (candidate === null || candidate === undefined) return reject("absent");
  const action = objectValue(candidate);
  if (!action) return reject("not_an_object");
  if (!hasCanonicalIdentity(action)) return reject("identity");
  if (!hasGovernedPresentation(action)) return reject("presentation");
  if (!hasValidPolicy(action)) return reject("policy");
  if (!hasEligiblePayload(action)) return reject("eligibility");
  return { action: candidate as NextAction, rejection: null, misroutedEnvelope };
}

/**
 * An action carries `action_id`; the wire envelope carries `next_action`. Only a
 * value that is NOT already action-shaped is unwrapped, so an action that also
 * happens to hold a `next_action` payload key is never mistaken for an envelope.
 */
function unwrapStreamEnvelope(value: unknown): {
  candidate: unknown;
  misroutedEnvelope: boolean;
} {
  const outer = objectValue(value);
  if (!outer) return { candidate: value, misroutedEnvelope: false };
  if ("action_id" in outer) return { candidate: outer, misroutedEnvelope: false };
  // Either half of the producer's envelope shape identifies it: the payload key
  // it wraps, or its SSE discriminator. An envelope whose action is missing
  // still resolves to `absent`, never to a bogus identity failure.
  if (STREAM_ENVELOPE_KEY in outer || outer.type === STREAM_ENVELOPE_KEY) {
    return { candidate: outer[STREAM_ENVELOPE_KEY] ?? null, misroutedEnvelope: true };
  }
  return { candidate: outer, misroutedEnvelope: false };
}

/**
 * Validate the untrusted HTTP/SSE action once at the client boundary.
 *
 * An action with a missing or divergent ID must never render: allowing the CTA
 * through would create an eligible decision that cannot be joined to its
 * impression, click, or payment. Callers report the handled degradation and
 * fall back to their existing non-authoritative UI.
 *
 * Thin projection of {@link resolveGovernedAction} for call sites that only need
 * the action. A call site that reports telemetry should use the full outcome so
 * the reason reaches the beacon.
 */
export function eligibleGovernedAction(value: unknown): NextAction | null {
  return resolveGovernedAction(value).action;
}

function hasCanonicalIdentity(action: Record<string, unknown>): boolean {
  const actionId = stringValue(action.action_id);
  const journeyActionId = stringValue(action.journey_action_id);
  const presentationKey = stringValue(action.presentation_key);
  return ACTION_ID.test(actionId) && actionId === journeyActionId && actionId === presentationKey;
}

function hasGovernedPresentation(action: Record<string, unknown>): boolean {
  const slot = stringValue(action.slot);
  const target =
    slot === "primary_monetization"
      ? stringValue(action.offer_id)
      : stringValue(action.destination);
  return Boolean(
    SLOTS.has(slot) &&
    target &&
    stringValue(action.kind) &&
    stringValue(action.lifecycle_stage) &&
    stringValue(action.reason_code) &&
    stringValue(action.renderer_key) &&
    stringValue(action.placement) &&
    stringValue(action.why)
  );
}

function hasValidPolicy(action: Record<string, unknown>): boolean {
  const policyId = stringValue(action.policy_id);
  const policyVersion = Number(action.policy_version);
  return Boolean(
    policyId &&
    policyId === stringValue(action.source_policy_id) &&
    Number.isInteger(policyVersion) &&
    policyVersion >= 1
  );
}

function hasEligiblePayload(action: Record<string, unknown>): boolean {
  const eligibility = objectValue(action.eligibility);
  return Boolean(
    eligibility?.eligible === true &&
    nonEmptyStrings(eligibility.reasons) &&
    objectValue(action.payload)
  );
}

/** Only attribute an action when its eligible offer is the offer rendered. */
export function governedActionForOffer(
  action: NextAction | null | undefined,
  offerId: string
): NextAction | null {
  return action?.offer_id === offerId ? action : null;
}

/** Camel-case shape owned by UI checkout contexts. */
export interface JourneyActionAnalyticsContext {
  journeyActionId?: string;
  presentationId?: string;
  concernId?: string;
  activeConcern?: string;
  journeySlot?: string;
  journeyStage?: string;
  journeyActionKind?: string;
  journeyPolicyId?: string;
  journeyPolicyVersion?: number;
}

/** Build one checkout context projection from the canonical wire action. */
export function governedActionAnalyticsContext(
  action: NextAction | null | undefined
): JourneyActionAnalyticsContext {
  if (!action) return {};
  return {
    journeyActionId: action.journey_action_id,
    presentationId: action.presentation_key,
    concernId: governedActionConcernId(action),
    activeConcern: payloadString(action.payload, "active_concern"),
    journeySlot: action.slot,
    journeyStage: action.lifecycle_stage,
    journeyActionKind: action.kind,
    journeyPolicyId: action.policy_id,
    journeyPolicyVersion: action.policy_version,
  };
}

/** Snake-case projection shared by every owned analytics event. */
export function journeyActionEventProperties(
  value: JourneyActionAnalyticsContext | null | undefined
) {
  const context = value ?? {};
  return {
    journey_action_id: context.journeyActionId,
    concern_id: context.concernId,
    journey_slot: context.journeySlot,
    journey_stage: context.journeyStage,
    journey_action_kind: context.journeyActionKind,
    journey_policy_id: context.journeyPolicyId,
    journey_policy_version: context.journeyPolicyVersion,
  };
}

export function governedActionEventProperties(action: NextAction | null | undefined) {
  return journeyActionEventProperties(governedActionAnalyticsContext(action));
}

export function canonicalJourneyActionId(value: unknown): string | undefined {
  const id = stringValue(value);
  return ACTION_ID.test(id) ? id : undefined;
}

export function canonicalConcernId(value: unknown): string | undefined {
  const id = stringValue(value);
  return CONCERN_ID.test(id) ? id : undefined;
}

/** Extract only a canonical concern identity from an optional action payload. */
export function governedActionConcernId(action: NextAction | null | undefined): string | undefined {
  return canonicalConcernId(payloadString(action?.payload, "concern_id"));
}

function objectValue(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function nonEmptyStrings(value: unknown): boolean {
  return (
    Array.isArray(value) && value.length > 0 && value.every((item) => Boolean(stringValue(item)))
  );
}

function payloadString(
  payload: NextAction["payload"] | undefined,
  key: string
): string | undefined {
  const value = payload?.[key];
  return typeof value === "string" && value.trim() ? value : undefined;
}
